import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from "react-router-dom";
import { crmService } from "../data-services";
import styles from './ThanksPage.module.css';
import { resolveAssetUrl } from "../utils/asset-url";
import { getCopyrightYearRange } from "../utils/copyright";

const parseTickets = (rawTickets) => {
    if (!rawTickets) {
        return [];
    }

    try {
        const parsed = typeof rawTickets === 'string' ? JSON.parse(rawTickets) : rawTickets;
        return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
        return [];
    }
};

const formatApiError = (error, fallbackMessage) => {
    const responseData = error?.response?.data;

    if (typeof responseData === 'string' && responseData.trim()) {
        return responseData;
    }

    if (responseData && typeof responseData === 'object') {
        if (typeof responseData.message === 'string' && responseData.message.trim()) {
            return responseData.message;
        }
    }

    if (typeof error?.message === 'string' && error.message.trim()) {
        return error.message;
    }

    return fallbackMessage;
};

const parseBlobErrorMessage = async (error, fallbackMessage) => {
    const responseData = error?.response?.data;
    if (responseData instanceof Blob) {
        try {
            const rawText = await responseData.text();
            if (!rawText) {
                return fallbackMessage;
            }

            try {
                const parsed = JSON.parse(rawText);
                if (typeof parsed?.message === 'string' && parsed.message.trim()) {
                    return parsed.message;
                }
            } catch (_) {
                // Keep raw response message fallback below.
            }

            if (rawText.trim()) {
                return rawText;
            }
        } catch (_) {
            return fallbackMessage;
        }
    }

    return formatApiError(error, fallbackMessage);
};

const ThanksPage = () => {
    const { order_id } = useParams();

    const [purchase, setPurchase] = useState();
    const [ticket, setTicket] = useState();
    const [tickets, setTickets] = useState([]);
    const [order, setOrder] = useState();
    const [loadingTicket, setLoadingTicket] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [fetchError, setFetchError] = useState('');
    const [downloadError, setDownloadError] = useState('');

    const copyrightYears = getCopyrightYearRange(2022);
    const totalAmount = useMemo(() => {
        const purchaseAmount = Number(purchase?.amount);
        if (Number.isFinite(purchaseAmount) && purchaseAmount > 0) {
            return purchaseAmount;
        }

        return tickets.reduce((acc, ticketItem) => {
            const itemSum = Number(ticketItem?.sum);
            return acc + (Number.isFinite(itemSum) ? itemSum : 0);
        }, 0);
    }, [purchase?.amount, tickets]);

    useEffect(() => {
        let isMounted = true;

        async function fetchData() {
            setFetchError('');
            setIsLoading(true);
            try {
                const { data } = await crmService.findOrder(order_id);
                if (!isMounted) {
                    return;
                }

                const nextPurchase = data?.purchase || {};
                const nextOrder = data?.order || {};
                setPurchase(nextPurchase);
                setTicket(data?.ticket || {});
                setTickets(parseTickets(nextOrder?.tickets));
                setOrder(nextOrder);
            } catch (error) {
                if (!isMounted) {
                    return;
                }
                setFetchError(formatApiError(error, 'Не вдалося завантажити дані квитка. Спробуйте оновити сторінку.'));
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        }

        fetchData();

        return () => {
            isMounted = false;
        };
    }, [order_id]);

    const handleCheckTicket = async (currentOrderId) => {
        try {
            setLoadingTicket(true);
            setDownloadError('');

            const response = await crmService.getTicketPdf(currentOrderId);
            const pdfBlob = response?.data;
            if (!(pdfBlob instanceof Blob) || pdfBlob.size === 0) {
                throw new Error('Сервер повернув порожній PDF файл.');
            }

            const objectUrl = window.URL.createObjectURL(pdfBlob);
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = `ticket-${currentOrderId}.pdf`;
            link.rel = 'noopener noreferrer';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.setTimeout(() => {
                window.URL.revokeObjectURL(objectUrl);
            }, 500);
        } catch (error) {
            const message = await parseBlobErrorMessage(error, 'Не вдалося завантажити квиток. Спробуйте ще раз.');
            setDownloadError(message);
            console.error('[thanks/download-ticket]', error);
        } finally {
            setLoadingTicket(false);
        }
    };

    return (
        <div className={styles["wrapper"]}>
            <header className={styles["header"]} id="header">
                <div className={styles["container"]}>
                    <a href="/" className={styles["header-logotype"]}>
                        <img src="/dino-logo.png" alt="Dino World" />
                    </a>
                </div>
            </header>
            <main className={styles["main-wrapper"]}>
                <section className={styles["thanks-section"]} id="thanks-section">
                    <div className={styles["container"]}>
                        <div className={styles["thanks-section__bg"]}>
                            <img loading="lazy" src="/dino-world-bg.jpg" alt="Dino World background" />
                        </div>
                        <div className={styles["thanks-section__wrapper"]}>
                            <h1 className={styles["thanks-section__title"]}>Дякуємо за покупку!</h1>

                            {fetchError ? (
                                <div className={styles["thanks-section__error"]}>{fetchError}</div>
                            ) : null}

                            {isLoading ? (
                                <div className={styles["thanks-section__loading"]}>Завантаження квитка...</div>
                            ) : (
                                <div className={styles["thanks-section__inner"]}>
                                <div className={styles["thanks-section__info"]}>
                                    <h2 className={styles["thanks-section__info-title"]}>{ticket?.event_title}</h2>
                                    <div className={styles["purchase-info"]}>
                                        <div className={`${styles["purchase-info__group"]} ${styles["purchase-info__group_half"]}`}>
                                            <div className={styles["purchase-info__label"]}>Назва білету</div>
                                            <div className={styles["purchase-info__value"]}>
                                                {ticket?.name || order?.ticket_name || '—'}
                                            </div>
                                        </div>
                                        <div className={`${styles["purchase-info__group"]} ${styles["purchase-info__group_half"]}`}>
                                            <div className={styles["purchase-info__label"]}>Сума</div>
                                            <div className={styles["purchase-info__value"]}>
                                                {totalAmount || 0} {ticket?.currency || 'UAH'}
                                            </div>
                                        </div>
                                        <div className={styles["purchase-info__group"]}>
                                            <div className={styles["purchase-info__label"]}>Імʼя</div>
                                            <div className={styles["purchase-info__value"]}>
                                                {purchase?.name || order?.name || '—'}
                                            </div>
                                        </div>
                                        <div className={styles["purchase-info__group"]}>
                                            <div className={styles["purchase-info__label"]}>Email</div>
                                            <div className={styles["purchase-info__value"]}>
                                                {purchase?.email || order?.email || '—'}
                                            </div>
                                        </div>
                                        <div className={styles["purchase-info__group"]}>
                                            <div className={styles["purchase-info__label"]}>Телефон</div>
                                            <div className={styles["purchase-info__value"]}>
                                                {order?.phone || '—'}
                                            </div>
                                        </div>

                                        {tickets?.map((ticketItem, index) => {
                                            return (
                                                <div
                                                    className={`${styles["purchase-info__group"]} ${styles["purchase-info__group_half"]}`}
                                                    key={`${ticketItem?.type || 'ticket'}-${index}`}
                                                >
                                                    <div className={styles["purchase-info__label"]}>{ticketItem?.type || 'Тип квитка'}</div>
                                                    <div className={styles["purchase-info__value"]}>{ticketItem?.count || 0}</div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div className={styles["thanks-section__image"]}>
                                    {ticket?.image ? (
                                        <img src={resolveAssetUrl(ticket?.image)} alt={ticket?.name || 'Ticket'} />
                                    ) : (
                                        <div className={styles["thanks-section__image-placeholder"]}>Зображення квитка</div>
                                    )}
                                </div>
                            </div>
                            )}
                            <div className={styles["thanks-section__actions"]}>
                                <button
                                    type="button"
                                    className={`${styles["thanks-section__button"]} ${styles["btn"]}`}
                                    disabled={loadingTicket || isLoading}
                                    onClick={() => handleCheckTicket(order_id)}
                                >
                                    {loadingTicket ? (
                                        <span className={styles["loader"]}></span>
                                    ) : (
                                        <>
                                            <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path
                                                    d="M22.0512 14.4746C21.8523 14.4746 21.6616 14.5536 21.5209 14.6943C21.3802 14.8349 21.3012 15.0257 21.3012 15.2246V17.7859C21.3012 18.3896 21.0614 18.9685 20.6345 19.3954C20.2077 19.8223 19.6287 20.0621 19.025 20.0621H5.97498C5.37128 20.0621 4.79231 19.8223 4.36543 19.3954C3.93855 18.9685 3.69873 18.3896 3.69873 17.7859V15.2246C3.69873 15.0257 3.61971 14.8349 3.47906 14.6943C3.33841 14.5536 3.14764 14.4746 2.94873 14.4746C2.74982 14.4746 2.55905 14.5536 2.4184 14.6943C2.27775 14.8349 2.19873 15.0257 2.19873 15.2246V17.7859C2.19972 18.7871 2.5979 19.747 3.30586 20.455C4.01383 21.1629 4.97376 21.5611 5.97498 21.5621H19.025C20.0262 21.5611 20.9861 21.1629 21.6941 20.455C22.4021 19.747 22.8002 18.7871 22.8012 17.7859V15.2246C22.8012 15.0257 22.7222 14.8349 22.5816 14.6943C22.4409 14.5536 22.2501 14.4746 22.0512 14.4746Z"
                                                    fill="white"
                                                />
                                                <path
                                                    d="M11.9673 16.665C12.037 16.7353 12.12 16.7911 12.2114 16.8292C12.3028 16.8672 12.4008 16.8868 12.4998 16.8868C12.5988 16.8868 12.6968 16.8672 12.7882 16.8292C12.8796 16.7911 12.9626 16.7353 13.0323 16.665L17.2998 12.3975C17.4189 12.2542 17.4803 12.0718 17.4723 11.8856C17.4642 11.6995 17.3872 11.5231 17.2561 11.3906C17.1251 11.2582 16.9495 11.1793 16.7635 11.1692C16.5774 11.1592 16.3943 11.2187 16.2498 11.3363L13.2498 14.3363V3.1875C13.2498 2.98859 13.1708 2.79782 13.0301 2.65717C12.8895 2.51652 12.6987 2.4375 12.4998 2.4375C12.3009 2.4375 12.1101 2.51652 11.9695 2.65717C11.8288 2.79782 11.7498 2.98859 11.7498 3.1875V14.325L8.74979 11.325C8.60906 11.1843 8.41819 11.1052 8.21917 11.1052C8.02014 11.1052 7.82927 11.1843 7.68854 11.325C7.54781 11.4657 7.46875 11.6566 7.46875 11.8556C7.46875 12.0546 7.54781 12.2455 7.68854 12.3862L11.9673 16.665Z"
                                                    fill="white"
                                                />
                                            </svg>
                                            <span>Завантажити PDF</span>
                                        </>
                                    )}
                                </button>
                            </div>
                            {downloadError ? <div className={styles["thanks-section__error"]}>{downloadError}</div> : null}
                        </div>
                    </div>
                </section>
            </main>

            <footer className={styles["footer"]} id="footer">
                <div className={styles["container"]}>
                    <div className={styles["footer-wrapper"]}>
                        <div className={styles["footer-logotype"]}>
                            <a href="/">
                                <img src="/dino-logo.png" alt="" />
                            </a>
                        </div>
                        <div className={styles["footer-navigation"]}>
                            <h3 className={styles["footer-title"]}>Інформація</h3>
                            <ul>
                                <li><button type="button">Умови і правила Оплати</button></li>
                                <li><button type="button">Умови і правила Доставки</button></li>
                                <li><button type="button">Умови і правила повернення коштів</button></li>
                                <li><button type="button">Умови Конфіденційності даних клієнта</button></li>
                                <li><button type="button">Про нас</button></li>
                                <li><button type="button">Договір оферти</button></li>
                            </ul>
                        </div>
                        <div className={styles["footer-contacts"]}>
                            <h3 className={styles["footer-title"]}>Контакти</h3>
                            <div className={styles["footer-contacts__links"]}>
                                <a href="tel:+380504489303" className={styles["footer-link"]}>+38 (050) 448 93 03</a>
                                <a href="mailto:dinoparkkyiv@gmail.com" className={styles["footer-link"]}>dinoparkkyiv@gmail.com</a>
                            </div>
                            <div className={styles["footer-payments"]}>
                                <div className={styles["footer-payments__image"]}>
                                    <img loading="lazy" width="91" height="20" src="/liqpay-icon.svg" alt="Liqpay" />
                                </div>
                                <div className={styles["footer-payments__image"]}>
                                    <img loading="lazy" width="60" height="20" src="/visa-icon.svg" alt="Visa" />
                                </div>
                                <div className={styles["footer-payments__image"]}>
                                    <img loading="lazy" width="34" height="26" src="/mastercard-icon.svg" alt="Mastercard" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={styles["footer-bottom"]}>
                        <div className={styles["footer-copyright"]}>Всі права захищені DINO WORLD © {copyrightYears}</div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default ThanksPage;
